'use client';

import type { ComponentProps } from 'react';
import { Pencil, Pill } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/lib/utils';

export interface RxDraftSummaryNodeProps extends ComponentProps<'section'> {
  medicationDisplayName: string;
  directions: string;
  quantity: string;
  refills: string;
  daysSupply: string;
  effectiveDate?: string;
  pharmacyDisplayName?: string;
  pharmacyNotes?: string;
  noSubstitutions?: boolean;
  diagnosis?: { code: string; name: string } | null;
  onEdit?: () => void;
  onEditDiagnosis?: () => void;
}

export function RxDraftSummaryNode({
  medicationDisplayName,
  directions,
  quantity,
  refills,
  daysSupply,
  effectiveDate,
  pharmacyDisplayName,
  pharmacyNotes,
  noSubstitutions,
  diagnosis,
  onEdit,
  onEditDiagnosis,
  className,
  ...props
}: RxDraftSummaryNodeProps) {
  const trimmedQuantity = quantity.trim();
  const trimmedRefills = refills.trim();
  const trimmedDaysSupply = daysSupply.trim();
  const trimmedEffectiveDate = effectiveDate?.trim() ?? '';
  const trimmedPharmacyDisplayName = pharmacyDisplayName?.trim() ?? '';
  const trimmedPharmacyNotes = pharmacyNotes?.trim() ?? '';
  return (
    <section className={cn('text-sm', className)} {...props}>
      <div className="flex items-start gap-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-[14px] bg-[#fff0e8] text-[#c85b29] dark:bg-[#44271c] dark:text-[#ffb18c]">
          <Pill className="size-6" strokeWidth={1.75} aria-hidden />
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          <div className="text-xs font-medium text-muted-foreground">Prescription</div>
          <h3 className="mt-1 text-xl font-medium leading-tight tracking-[-0.01em]">
            {medicationDisplayName}
          </h3>
        </div>

        {onEdit ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onEdit}
            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          >
            <Pencil className="size-3" aria-hidden />
            Edit
          </Button>
        ) : null}
      </div>

      <div className="mt-7 space-y-5">
        <TextRow label="Directions" value={directions || 'Not specified'} />
        <div className="grid grid-cols-3 gap-4">
          <TextRow label="Quantity" value={trimmedQuantity || '—'} />
          <TextRow
            label="Refills"
            value={trimmedRefills === '0' ? 'None' : trimmedRefills || '—'}
          />
          <TextRow label="Days supply" value={trimmedDaysSupply || '—'} />
        </div>
        <TextRow label="Start date" value={trimmedEffectiveDate || '—'} />
        <TextRow label="Pharmacy" value={trimmedPharmacyDisplayName || 'No pharmacy selected'} />
        {diagnosis ? (
          <div className="flex items-start justify-between gap-4 pb-3">
            <div className="min-w-0">
              <div className="text-xs font-medium text-muted-foreground">Diagnosis</div>
              <div className="mt-1 whitespace-pre-wrap text-[15px] font-medium leading-5 text-foreground">
                {diagnosis.name || diagnosis.code}
              </div>
              {diagnosis.name ? (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
                    ICD-10
                  </span>
                  <span className="rounded-md bg-secondary/70 px-2 py-0.5 font-mono text-xs font-medium text-foreground/85">
                    {diagnosis.code}
                  </span>
                </div>
              ) : null}
            </div>
            {onEditDiagnosis ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onEditDiagnosis}
                className="h-7 shrink-0 gap-1 px-2 text-xs text-muted-foreground"
              >
                <Pencil className="size-3" aria-hidden />
                Edit
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {trimmedPharmacyNotes || noSubstitutions ? (
        <details className="group mt-5 text-sm">
          <summary className="cursor-pointer list-none py-1 text-muted-foreground hover:text-foreground">
            More prescription details
          </summary>
          <div className="mt-3 space-y-4 border-l border-border/60 pl-4">
            <TextRow
              label="Substitutions"
              value={noSubstitutions ? 'Do not substitute' : 'Generic substitution allowed'}
            />
            {trimmedPharmacyNotes ? (
              <TextRow label="Pharmacy note" value={trimmedPharmacyNotes} />
            ) : null}
          </div>
        </details>
      ) : null}
    </section>
  );
}

function TextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 whitespace-pre-wrap text-[15px] font-medium leading-5 text-foreground">
        {value}
      </div>
    </div>
  );
}
